import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { useEditResellerMutation } from "../../store/services/customerApi";

const EditTMDetail = ({ isOpen, onClose, customerData }) => {
  const [domainName, setDomainName] = useState("");
  const [telemarketerID, setTelemarketerID] = useState("");
  const [selectedOption, setSelectedOption] = useState("");
  const [error, setError] = useState("");

  const [editReseller, { isLoading }] = useEditResellerMutation();

  useEffect(() => {
    if (isOpen) {
      setDomainName("");
      setTelemarketerID("");
      setSelectedOption("");
      setError("");
    }
  }, [isOpen]);

  const validateInputs = () => {
    if (!domainName || !telemarketerID || !selectedOption) {
      setError("All fields are required.");
      toast.error("All fields are required.");
      return false;
    }

    if (!domainName.match(/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)) {
      setError("Invalid domain name. Example: example.com");
      toast.error("Invalid domain name. Example: example.com");
      return false;
    }

    if (!telemarketerID.match(/^[0-9]+$/)) {
      setError("Telemarketer ID must be numeric.");
      toast.error("Telemarketer ID must be numeric.");
      return false;
    }

    if (!customerData?.customer_id) {
      setError("Customer ID is missing.");
      toast.error("Customer ID is missing. Cannot proceed.");
      return false;
    }

    setError("");
    return true;
  };

  const handleSubmit = async () => {
    if (!validateInputs()) return;

    const payload = {
      reseller_name: customerData?.name || "Default Reseller",
      reseller_domain: domainName,
      TM_ID: parseInt(telemarketerID, 10),
      TM_ID_type: selectedOption,
      customer_id: customerData.customer_id,
      is_active: false,
    };

    try {
      await editReseller(payload).unwrap();
      toast.success("Reseller profile updated successfully!");
      onClose();
    } catch (err) {
      const errorMessage = err.data?.message || "An error occurred while updating the reseller profile.";
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{
      sx: {
        '&.MuiPaper-root': {
          left: 40,
        },
      },
    }}>
      <DialogTitle sx={{ fontSize: '1rem', pb: 0 }}>Edit Reseller</DialogTitle>
      <DialogContent>
        <Typography variant="body2" align="center" color="textSecondary" gutterBottom sx={{ fontSize: '0.7rem' }}
        >
          Please update the Domain Name and Telemarketer ID for this customer.
        </Typography>
        <Typography variant="body2" align="center" color="error" gutterBottom sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}
        >
          NOTE: Ensure the correct information is provided to avoid issues. Incorrect entries may result in errors.
        </Typography>

        <TextField
          fullWidth
          label="Domain Name"
          value={domainName}
          onChange={(e) => setDomainName(e.target.value)}
          variant="outlined"
          margin="normal"
          error={!!error && error.includes("domain")}
          helperText="Enter a valid domain (e.g. example.com)"
          size="small"
          InputLabelProps={{ sx: { fontSize: '0.7rem' } }}
          inputProps={{ style: { fontSize: '0.7rem' } }}
          FormHelperTextProps={{ sx: { fontSize: '0.65rem' } }}
        />
        <TextField
          fullWidth
          label="Telemarketer ID"
          value={telemarketerID}
          onChange={(e) => setTelemarketerID(e.target.value)}
          variant="outlined"
          margin="normal"
          error={!!error && error.includes("Telemarketer ID")}
          helperText="Telemarketer ID must be numeric."
          size="small"
          InputLabelProps={{ sx: { fontSize: '0.7rem' } }}
          inputProps={{ style: { fontSize: '0.7rem' } }}
          FormHelperTextProps={{ sx: { fontSize: '0.65rem' } }}
        />

        <Typography variant="body2" color="error" gutterBottom>
          TM ID Type:
        </Typography>
        <RadioGroup
          row
          value={selectedOption}
          onChange={(e) => setSelectedOption(e.target.value)}
          sx={{ fontSize: '0.7rem' }}

        >
          <FormControlLabel
            value="Aggregator"
            control={<Radio />}
            label={<span style={{ fontSize: '0.7rem' }}>Aggregator</span>}
          />
          <FormControlLabel
            value="Delivery"
            control={<Radio />}
            label={<span style={{ fontSize: '0.7rem' }}>Delivery</span>}
          />
        </RadioGroup>

        {error && (
          <Typography color="error" align="center" variant="body2" sx={{ fontSize: '0.7rem' }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="contained" color="primary" onClick={handleSubmit} disabled={isLoading} sx={{ fontSize: '0.7rem', height: 30 }}
        >
          {isLoading ? "Updating..." : "Submit"}
        </Button>
        <Button color="primary" onClick={onClose} sx={{ fontSize: '0.7rem', height: 30 }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTMDetail;
